import { generatePath, Outlet, useNavigate, useParams } from "react-router-dom";

import { LoadingState, SectionMenu } from "~/common/components";
import { SessionHeader } from "~/providers/components/patientProfile/sessions/SessionHeader";
import { SessionSummaryViewer } from "~/providers/components/patientProfile/sessions/SessionSummaryViewer";
import {
  useGetPatientZoomMeeting,
  useGetPatientZoomMeetingBillingNote,
  useGetPatientZoomMeetingSoapNote,
  useGetPatientZoomMeetingSummary,
} from "~/providers/hooks/queries/useGetPatientZoomMeeting";
import { useUpdatePatientZoomMeetingSessionType } from "~/providers/hooks/queries/useUpdatePatientZoomMeetingSessionType";
import { ROUTES } from "~/providers/router/routes";
import { getSessionMenuOptions } from "~/providers/utils/getSessionMenuOptions";

export const SessionLayout = () => {
  const navigate = useNavigate();
  const { patientId, sessionId } = useParams();
  const hasRouteParams = Boolean(patientId && sessionId);

  const { data: session, isLoading } = useGetPatientZoomMeeting({
    patientId: patientId ?? "",
    sessionId: sessionId ?? "",
    enabled: hasRouteParams,
  });

  const { data: summary, isLoading: isLoadingSummary } =
    useGetPatientZoomMeetingSummary({
      patientId: patientId ?? "",
      sessionId: sessionId ?? "",
      summaryStatus: session?.summary_status,
      enabled: hasRouteParams && !!session,
    });

  const { data: soapNote } = useGetPatientZoomMeetingSoapNote({
    patientId: patientId ?? "",
    sessionId: sessionId ?? "",
    enabled: hasRouteParams,
  });

  const { data: billingNote } = useGetPatientZoomMeetingBillingNote({
    patientId: patientId ?? "",
    sessionId: sessionId ?? "",
    enabled: hasRouteParams,
  });

  const { mutate: updateSessionType, isPending: isUpdatingSessionType } =
    useUpdatePatientZoomMeetingSessionType(patientId ?? "");

  if (!hasRouteParams) return null;

  const sessionsPath = generatePath(ROUTES.sessions, { patientId: patientId! });

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center px-14 py-8">
        <LoadingState />
      </div>
    );
  }

  if (!session) {
    return (
      <div className="px-14 py-8 text-sm text-gray-70">Session not found.</div>
    );
  }

  const isFollowUpMeeting = session.session_type === "FOLLOW_UP";

  const menuOptions = getSessionMenuOptions(
    navigate,
    patientId!,
    sessionId!,
    Boolean(soapNote),
    Boolean(billingNote),
    isFollowUpMeeting,
  );

  return (
    <div className="flex w-full flex-col gap-3 px-14 py-4">
      <SessionHeader
        sessionsPath={sessionsPath}
        session={session}
        isUpdatingSessionType={isUpdatingSessionType}
        onSessionTypeChange={(sessionType) =>
          updateSessionType({
            sessionId: sessionId!,
            session_type: sessionType,
          })
        }
      />

      <div className="flex flex-col gap-3">
        <SessionSummaryViewer
          summaryStatus={session.summary_status}
          presignedUrl={summary?.presigned_url}
          isLoadingSummary={isLoadingSummary}
        />
      </div>

      <div className="sticky top-0 z-20 flex w-full flex-col py-2">
        <SectionMenu menuOptions={menuOptions} />
      </div>

      <Outlet />
    </div>
  );
};
